import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const nowLocal = () => format(new Date(), "yyyy-MM-dd'T'HH:mm");
const empty = (clientId) => ({
  client_id: clientId || "",
  occurred_at: nowLocal(),
  what_happened: "",
  action_taken: "",
  notified: "",
  outcome: "",
  follow_up: "",
  resolved: false,
  alert_family: true,
});

export function IncidentDialog({ open, onOpenChange, clients, initialClientId, onSaved }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(empty(initialClientId || clients[0]?.id || ""));
  }, [open, initialClientId, clients]);

  const save = async () => {
    if (!form.client_id || !form.what_happened.trim()) return toast.error("Client and what happened are required.");
    setSaving(true);
    try {
      const { data } = await api.post("/portal/incidents", form);
      toast.success(data.alert_sent ? "Incident logged — family alerted by email" : "Incident logged");
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Could not save the incident.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-sand max-w-lg max-h-[90vh] overflow-y-auto" data-testid="incident-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Log Incident</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="incident-client" className="mt-1.5 bg-white"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>When</Label><Input type="datetime-local" data-testid="incident-when" value={form.occurred_at} onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} className="mt-1.5 bg-white" /></div>
          </div>
          <div><Label>What happened</Label><Textarea data-testid="incident-what" value={form.what_happened} onChange={(e) => setForm({ ...form, what_happened: e.target.value })} className="mt-1.5 bg-white resize-none" rows={2} placeholder="e.g. Minor fall in the kitchen" /></div>
          <div><Label>What you did</Label><Textarea value={form.action_taken} onChange={(e) => setForm({ ...form, action_taken: e.target.value })} className="mt-1.5 bg-white resize-none" rows={2} placeholder="Heimlich, first aid, helped up…" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>911 / notified</Label><Input value={form.notified} onChange={(e) => setForm({ ...form, notified: e.target.value })} className="mt-1.5 bg-white" placeholder="EMS, family, doctor" /></div>
            <div><Label>Outcome</Label><Input value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className="mt-1.5 bg-white" placeholder="Resolved on site / ER" /></div>
          </div>
          <div><Label>Follow-up needed</Label><Input value={form.follow_up} onChange={(e) => setForm({ ...form, follow_up: e.target.value })} className="mt-1.5 bg-white" /></div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer">
              <input type="checkbox" checked={form.resolved} onChange={(e) => setForm({ ...form, resolved: e.target.checked })} className="h-4 w-4 accent-sage" /> Resolved
            </label>
            <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer">
              <input type="checkbox" data-testid="incident-alert" checked={form.alert_family} onChange={(e) => setForm({ ...form, alert_family: e.target.checked })} className="h-4 w-4 accent-terracotta" /> Email family immediately
            </label>
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-incident" className="mt-2 w-full rounded-full bg-terracotta text-white py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-60">
          {saving ? "Saving…" : "Save & Alert Family"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
